/* eslint-disable @typescript-eslint/no-non-null-assertion */
/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { VerifyStep } from "./VerifyStep";
import {
  AppAlert,
  LaddaButton,
  RecaptchaTermNoTranslate,
  RecaptchaTokenAction,
  ConnectButton,
  SocialProvider,
  IBrowserError,
  RECAPTCHA_RETRY_DELAY_MS,
  RECAPTCHA_MAX_RETRIES,
  getRecaptchaToken,
  useCaptcha,
  IErrorMessage,
  RECAPTCHA_SCRIPT_LOAD_RETRIES,
  ERROR_CODE,
  FaroFeatures,
  useLogger,
  FARO_EVENTS,
  faroLogger,
} from "@advapacs/common";
import { useSelector } from "react-redux";
import { IRootState } from "@src/config/reducers";
import { useAppDispatch } from "@src/config/store";
import { getConfiguration, sendOTPToken } from "./ducks/operations";
import { UnauthenticatedActions } from "./ducks/slices";
import { useParams } from "react-router-dom";
import { PatientLogo } from "@src/components/PatientLogo";
import { useLogoUrl } from "@src/hooks/useLogoUrl";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { SuspicousActivity } from "@src/pages/unauthenticated/SuspicousActivity";
import { VerifyActivitySuccess } from "@src/pages/unauthenticated/VerifyActivitySuccess";
import { VerifyActivityFail } from "@src/pages/unauthenticated/VerifyActivityFail";
import { LoginAction } from "@src/reducers/slices/loginSlice";
import { useDetectSmallScreen } from "@src/hooks/useDetectSmallScreen";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { openWindowOpenOauthURL } from "@src/utils/utils";
import { prodModeLog, reCaptchaDelay } from "@advapacs/common/src/utils/utils";
import {
  GOOGLE_RECAPTCHA_ENTERPRISE,
  TURNSTILE_SITE_KEY,
} from "@src/config/constants";
import { LogLevel } from "@grafana/faro-web-sdk";

interface LoginFormProps {
  className?: string;
}

export enum OTPType {
  SMS = "SMS",
  EMAIL = "EMAIL",
}

export const LoginForm: FC<LoginFormProps> = ({ className = "" }) => {
  // ==================================== STATES ==============================
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { token } = useParams();
  const logoUrl = useLogoUrl();
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location]
  );
  const captchaType = useCaptcha();
  const turnstileRef = useRef<HTMLDivElement>(null);
  const tempTurnstileAction = useRef({
    action: "",
    data: {
      token: "",
    },
    challenge: "",
  });

  const [showErrorMessage, setShowErrorMessage] = useState(false); // hide the previous error while a new request is sent.
  const { executeRecaptcha } = useGoogleReCaptcha();
  const executeRecaptchaRef = useRef(executeRecaptcha);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [isEmail, setIsEmail] = useState(false);
  const [loginFailMessage, setLoginFailMessage] = useState("");
  const [isSms, setIsSms] = useState(false);
  const [turnstileError, setTurnstileError] = useState("");

  const {
    configuration,
    sendOTPLoading,
    errorMessage,
    sendOTPSuccess,
    suspiciousActivity,
    suspiciousActivitySuccess,
    suspiciousActivityFail,
  } = useSelector((state: IRootState) => state.unAuthenticatedReducer);
  const isSmallScreen = useDetectSmallScreen();
  const [browserError, setBrowserError] = useState<IBrowserError | null>(null);
  const verifySuspicousActivity = useMemo(() => {
    return suspiciousActivity && (isSms || isEmail);
  }, [isSms, isEmail, suspiciousActivity]);

  const verifyStep = useMemo(() => {
    return sendOTPSuccess && (isSms || isEmail);
  }, [isSms, isEmail, sendOTPSuccess]);

  // ==================================== LOGGER ==============================
  const logger = useLogger("[LoginForm]");

  // ==================================== FUNCTIONS ==============================

  const handleBrowserError = (err: IErrorMessage | string) => {
    const errMsg =
      typeof err === "string" ? err : err?.message || "Unknown error messsage";
    setBrowserError({ message: errMsg });
  };

  const generateRecaptchaToken = async (
    action: RecaptchaTokenAction,
    retries = RECAPTCHA_MAX_RETRIES
  ): Promise<string> => {
    setBrowserError(null); // reset error message when starting generate token.
    setCaptchaLoading(true);
    const currentExecuteRecaptcha = executeRecaptchaRef.current; // alway fresh

    // Script not loaded yet — retry until ready, then give up
    if (!currentExecuteRecaptcha) {
      if (retries > 0) {
        console.warn("Re-trigger waiting gRecaptcha loaded.");
        await reCaptchaDelay(RECAPTCHA_SCRIPT_LOAD_RETRIES);
        return generateRecaptchaToken(action, retries - 1);
      }
      setCaptchaLoading(false);
      logger({
        message: "reCAPTCHA script not loaded yet.",
        level: LogLevel.INFO,
        action: "generateRecaptchaToken",
        feature: FaroFeatures.RECAPTCHA,
      });
      return Promise.reject(new Error("reCAPTCHA script not loaded yet."));
    }
    try {
      return await getRecaptchaToken(
        action,
        GOOGLE_RECAPTCHA_ENTERPRISE,
        currentExecuteRecaptcha
      );
    } catch (error) {
      if (retries > 0) {
        const delayTime =
          RECAPTCHA_RETRY_DELAY_MS * (RECAPTCHA_MAX_RETRIES - retries + 1); // linear/exponential backoff
        console.warn(
          `reCAPTCHA failed. Retrying in ${
            delayTime / 1000
          }s... (${retries} retries left)`
        );
        await reCaptchaDelay(delayTime);
        return generateRecaptchaToken(action, retries - 1);
      }
      setCaptchaLoading(false);
      logger({
        level: LogLevel.ERROR,
        error: `reCAPTCHA token failed after retries: ${
          (error as IErrorMessage)?.message || error
        }`,
        action: "generateRecaptchaToken",
        feature: FaroFeatures.RECAPTCHA,
        args: { action },
      });
      return Promise.reject((error as IErrorMessage)?.message || error);
    }
  };

  const validateTurnstile = () => {
    if (!turnstileRef?.current || !window.turnstile) {
      setCaptchaLoading(false);
      return false;
    }
    return true;
  };

  const handleSendOTPViaEmail = async () => {
    try {
      // hide the previous error while sending again
      setShowErrorMessage(false);
      setIsEmail(true);
      setIsSms(false);

      if (captchaType?.provider === "turnstile") {
        if (!validateTurnstile()) {
          setTurnstileError("Turnstile not loaded in client Side.");
          return;
        }
        // handle turnstile.
        setTurnstileError(""); // reset error.
        // turnstile flow.
        window.turnstile?.reset(turnstileRef.current!); // reset before start
        tempTurnstileAction.current.action = "handleSendOTPViaEmail"; //
        tempTurnstileAction.current.data = {
          token: configuration?.token as string,
        };
        // trigger invisible challenge
        setCaptchaLoading(true); // trigger loading state.
        window.turnstile?.execute(turnstileRef.current!, {
          action: RecaptchaTokenAction.PATIENT_LOGIN_SEND_OTP,
          async: false, // prevent async retries
        });

        prodModeLog(
          "Trigger execute generate function from turnstile, (handleSendOTPViaEmail)"
        );
      } else {
        try {
          // avoid exception when trying generate recaptcha without ff.
          const recaptchaToken = await generateRecaptchaToken(
            RecaptchaTokenAction.PATIENT_LOGIN_SEND_OTP
          );

          if (!recaptchaToken) throw Error("Missing Recaptcha Token");
          dispatch(
            sendOTPToken(configuration?.token, OTPType.EMAIL, recaptchaToken)
          );
          setCaptchaLoading(false);
        } catch (error) {
          handleBrowserError(error as IErrorMessage);
        }
      }
    } catch (error) {
      setIsEmail(false);
      setIsSms(false);

      setCaptchaLoading(false);
    }
  };

  const handleSendOTPViaPhone = async () => {
    try {
      // hide the previous error while sending again
      setShowErrorMessage(false);
      setIsSms(true);
      setIsEmail(false);

      if (captchaType?.provider === "turnstile") {
        if (!validateTurnstile()) {
          setTurnstileError("Turnstile not loaded in client Side.");
          return;
        }
        // handle turnstile.
        setTurnstileError(""); // reset error.
        // turnstile flow.
        window.turnstile?.reset(turnstileRef.current!); // reset before start
        tempTurnstileAction.current.action = "handleSendOTPViaPhone"; //
        tempTurnstileAction.current.data = {
          token: configuration?.token as string,
        };
        // trigger invisible challenge
        setCaptchaLoading(true); // trigger loading state.
        window.turnstile?.execute(turnstileRef.current!, {
          action: RecaptchaTokenAction.PATIENT_LOGIN_SEND_OTP,
          async: false,
        });

        prodModeLog(
          "Trigger execute generate function from turnstile, (handleSendOTPViaPhone)"
        );
      } else {
        try {
          // avoid exception when trying generate recaptcha without ff.
          const recaptchaToken = await generateRecaptchaToken(
            RecaptchaTokenAction.PATIENT_LOGIN_SEND_OTP
          );
          if (!recaptchaToken) throw Error("Missing Recaptcha Token");
          dispatch(
            sendOTPToken(configuration?.token, OTPType.SMS, recaptchaToken)
          );
          setCaptchaLoading(false);
        } catch (error) {
          handleBrowserError(error as IErrorMessage);
        }
      }
    } catch (error) {
      setIsEmail(false);
      setIsSms(false);

      setCaptchaLoading(false);
    }
  };

  const handleSocialLogin = async (type: string) => {
    faroLogger.event(FARO_EVENTS.SOCIAL_LOGIN, {
      provider: type.toLowerCase(),
    });
    try {
      const rs = await axios.get(
        `/auth/oauth/${configuration?.token}/redirect/${type}`
      );
      openWindowOpenOauthURL(rs?.data?.redirectTo);
    } catch {}
  };

  const handleCallbackFunction = (turnstileToken: string) => {
    switch (tempTurnstileAction.current.action) {
      case "handleSendOTPViaEmail":
        dispatch(
          sendOTPToken(
            tempTurnstileAction.current.data?.token as string, // form data
            OTPType.EMAIL, // redirect path
            turnstileToken, // captcha token from google or turnstile
            true // trigger set header to request for turnstile or recaptcha
          )
        );

        break;

      case "handleSendOTPViaPhone":
        dispatch(
          sendOTPToken(
            tempTurnstileAction.current.data?.token as string, // form data
            OTPType.SMS, // redirect path
            turnstileToken, // captcha token from google or turnstile
            true // trigger set header to request for turnstile or recaptcha
          )
        );

        break;
      default:
        break;
    }
    prodModeLog("Finish function " + tempTurnstileAction.current.action);
    // reset after callback called.
    window.turnstile?.reset(turnstileRef.current!); // reset
    tempTurnstileAction.current.action = "";
    tempTurnstileAction.current.challenge = "";
    tempTurnstileAction.current.data.token = "";

    setCaptchaLoading(false);
  };
  // effects
  useEffect(() => {
    if (errorMessage?.code === "429") {
      logger({
        level: LogLevel.WARN,
        message: "Send OTP rate limited.",
        action: "sendOTP",
        feature: FaroFeatures.RECAPTCHA,
      });
      setCaptchaLoading(false);
      dispatch(UnauthenticatedActions.stopLoading());
      return;
    }

    // 1050 = RecaptchaInvalidToken. No retry here: send OTP is rate limited per link (3/10 min) and rejected requests count.
    if (
      errorMessage?.code === ERROR_CODE.RECAPTCHA_INVALID ||
      errorMessage?.code === "1051"
    ) {
      logger({
        level: LogLevel.WARN,
        message: `Send OTP rejected by captcha (${errorMessage.code}).`,
        action: "sendOTP",
        feature: FaroFeatures.RECAPTCHA,
        args: { provider: captchaType?.provider },
      });
    }
    setShowErrorMessage(true); // state, not a ref, so the alert re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMessage, isEmail, isSms]);

  useEffect(() => {
    return () => {
      dispatch(UnauthenticatedActions.clearErrorMessage());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (token) {
      localStorage.setItem("pp-token", token);
      dispatch(LoginAction.setIsShowEulaAfterLogin(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    executeRecaptchaRef.current = executeRecaptcha;
  }, [executeRecaptcha]);

  // recaptcha effect.
  useEffect(() => {
    if (captchaType?.provider === "turnstile") {
      /**
       * The Turnstile token is one-time use — it expires quickly (typically within 2 minutes).
        After sending it to your backend (or after any failed verification), you’ll usually need to refresh / reset the widget to generate a new token.
       */

      const interval = setInterval(() => {
        if (
          typeof window !== "undefined" &&
          window?.turnstile &&
          turnstileRef?.current
        ) {
          // eslint-disable-next-line no-console
          console.log("call interval !!!");
          clearInterval(interval);

          if (turnstileRef.current.hasChildNodes()) {
            // avoid call re-render multiple times
            console.warn(
              "Cloudflare Turnstile has already been rendered. Prevent !!!"
            );
            return;
          }
          window.turnstile?.render(turnstileRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            size: "invisible",
            callback: (turnstileToken: string) => {
              if (tempTurnstileAction.current.action !== "") {
                prodModeLog(
                  "Callback token from turnstile, will trigger function " +
                    tempTurnstileAction.current.action
                );
                handleCallbackFunction(turnstileToken);
              } else {
                prodModeLog("No action with callback function !!!");
              }
            },
            "expired-callback": () => {
              setCaptchaLoading(false);
              window.turnstile?.reset(turnstileRef.current!);
            },
            "error-callback": (turnstileErrorCode) => {
              // https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
              setCaptchaLoading(false);
              // eslint-disable-next-line no-console
              console.log({ turnstileErrorCode });
              // window.turnstile?.reset(turnstileRef.current!); // it will trigger retries when error callback.

              setTurnstileError(
                `Cloudflare Turnstile get error with code: ${turnstileErrorCode}`
              );
            },
          });
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captchaType]);

  const showBanner = useMemo(() => {
    return logoUrl && !isSmallScreen && !verifySuspicousActivity;
  }, [logoUrl, isSmallScreen, verifySuspicousActivity]);

  const externalIdps = useMemo(() => {
    const idps = configuration?.authentication?.externalIdps || [];
    return idps.filter((type) => {
      if (type === SocialProvider.GOOGLE) return true;
      if (type === SocialProvider.FACEBOOK) return true;
      if (type === SocialProvider.APPLE) return true;
    });
  }, [configuration]);

  return (
    <>
      <div ref={turnstileRef}></div>
      <div className={`${className} h-100`}>
        <div
          className={`text-center portal-form ${
            isSmallScreen ? "small-screen" : ""
          }`}
        >
          {showBanner && (
            <div className="flex-center mb-2">
              <PatientLogo
                className="flex-center w-100"
                // 16 : 9 ratio
                width={220}
                height={160}
                logoUrl={logoUrl as string}
                type="contain"
                lazyImageFulLWidth // the logo with contain type have container is 100%
              />
            </div>
          )}

          {!verifySuspicousActivity && !verifyStep && (
            <>
              <div>
                <h3 className="font-weight">{configuration?.name}</h3>
                <h5 className="m-b-sm ">Patient Portal Login</h5>
              </div>
              <div className="m-b-xs">
                <>
                  {externalIdps?.map((type, idx) => {
                    return (
                      <ConnectButton
                        lable="Continue"
                        className={`d-block w-100 m-b-${
                          idx === externalIdps.length - 1 ? "md" : "xs"
                        }`}
                        type={type as SocialProvider}
                        onConnect={() => handleSocialLogin(type)}
                        disabled={captchaLoading}
                      />
                    );
                  })}
                  {externalIdps?.length > 0 && (
                    <div className="middle-line">
                      <span className="text-muted">OR</span>
                    </div>
                  )}
                </>
              </div>

              <p className="m-b-sm">
                Sign in with OTP (One-time Password). Please select a method to
                receive your code:
              </p>

              {configuration?.authentication?.authenticationChannels?.includes(
                OTPType.EMAIL
              ) && (
                <div>
                  <LaddaButton
                    className={`btn btn-${
                      configuration?.authentication?.channelPreference ===
                      OTPType.EMAIL
                        ? "primary"
                        : "light"
                    } w-100`}
                    onClickHandler={handleSendOTPViaEmail}
                    btnName={"Send OTP via email"}
                    loading={sendOTPLoading || captchaLoading}
                  />
                </div>
              )}

              {configuration?.authentication?.authenticationChannels?.includes(
                OTPType.SMS
              ) && (
                <div className="m-t-sm">
                  <LaddaButton
                    className={`btn btn-${
                      configuration?.authentication?.channelPreference ===
                      OTPType.SMS
                        ? "primary"
                        : "light"
                    } w-100`}
                    onClickHandler={handleSendOTPViaPhone}
                    btnName={"Send OTP via SMS"}
                    loading={sendOTPLoading || captchaLoading}
                  />
                </div>
              )}

              <RecaptchaTermNoTranslate className="m-t-xs text-left" />

              {configuration?.authentication?.authenticationChannels?.length ===
                0 && (
                <AppAlert.SimpleAlert
                  variant="warning"
                  message="Your provided contact information is not compatible with the authentication method supported by the provider."
                />
              )}
              {errorMessage && (
                <div className="m-t-sm ">
                  {/*unsupported_sms_country_exception*/}
                  {errorMessage.code === "3405" && (
                    <AppAlert.SimpleAlert
                      variant="warning"
                      className="text-left "
                      // @ts-ignore
                      message={`Your provided country code +${errorMessage?.params?.countryCode} is not supported. Please choose a different communication channel or contact your service provider for further assistance.`}
                    />
                  )}
                  {/*rated limit exception*/}
                  {errorMessage.code === "429" && (
                    <AppAlert.SimpleAlert
                      variant="warning"
                      className="text-left "
                      message={
                        "Unable to resend OTP due to too many attempts. Please try again later."
                      }
                    />
                  )}
                  {/*other errors*/}
                  {errorMessage &&
                    !["3405", "429"].includes(errorMessage?.code as string) &&
                    showErrorMessage && (
                      <AppAlert.SimpleAlert
                        variant="warning"
                        className="text-left "
                        message={errorMessage.message}
                      />
                    )}
                </div>
              )}
              {turnstileError && (
                <AppAlert.SimpleAlert
                  variant="warning"
                  className="text-left"
                  message={turnstileError}
                />
              )}
              {browserError && (
                <AppAlert.SimpleAlert
                  variant="warning"
                  className="text-left"
                  message={browserError.message}
                />
              )}
              {queryParams.get("eulaWarnMessage") && (
                <div className="m-t-sm ">
                  <AppAlert.SimpleAlert
                    variant="warning"
                    className="text-left "
                    message={t("form:eula_message")}
                  />
                </div>
              )}
            </>
          )}

          {verifyStep && (
            <VerifyStep
              sms={isSms}
              editOtpCode={() => {
                dispatch(UnauthenticatedActions.clearSendOTPMethod());
                if (token) dispatch(getConfiguration(token));
                setIsEmail(false);
                setIsSms(false);
                dispatch(UnauthenticatedActions.clearSendOTPMethod());
                setCaptchaLoading(false);
              }}
            />
          )}

          {verifySuspicousActivity &&
            !suspiciousActivitySuccess &&
            !suspiciousActivityFail && (
              <SuspicousActivity
                sms={isSms}
                goBack={() => {
                  // reset loading state.
                  dispatch(UnauthenticatedActions.clearSendOTPMethod());
                  setCaptchaLoading(false);
                  // dispatch new token
                  if (token) dispatch(getConfiguration(token));
                  // reset type of login form.
                  setIsEmail(false);
                  setIsSms(false);
                }}
              />
            )}

          {suspiciousActivitySuccess && <VerifyActivitySuccess />}
          {suspiciousActivityFail && <VerifyActivityFail />}

          {loginFailMessage && (
            <AppAlert.SimpleAlert
              variant="danger"
              message={loginFailMessage}
              dismissible
              onClose={() => setLoginFailMessage("")}
            />
          )}
        </div>
      </div>
    </>
  );
};
